import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { lineIsCartesian, wallIsCartesian } from './checkCartesian'

export type CartesianAxis = 'x' | 'y'

/** Minimum collinear overlap length (in m) to treat a beam as lying on a wall. */
export const MIN_BEAM_WALL_OVERLAP_LENGTH = 0.05

export interface XYPoint {
  x: number
  y: number
}

export interface CartesianSegment {
  start: XYPoint
  end: XYPoint
}

export const getCartesianAxis = (start: XYPoint, end: XYPoint): CartesianAxis | null => {
  if (start.x === end.x) return 'y'
  if (start.y === end.y) return 'x'
  return null
}

export const getCartesianSegmentsOverlapLength = (
  a: CartesianSegment,
  b: CartesianSegment,
): number | null => {
  const axisA = getCartesianAxis(a.start, a.end)
  const axisB = getCartesianAxis(b.start, b.end)

  if (!axisA || !axisB || axisA !== axisB) return null

  if (axisA === 'x') {
    if (a.start.y !== b.start.y) return null

    const aMin = Math.min(a.start.x, a.end.x)
    const aMax = Math.max(a.start.x, a.end.x)
    const bMin = Math.min(b.start.x, b.end.x)
    const bMax = Math.max(b.start.x, b.end.x)
    const overlapMin = Math.max(aMin, bMin)
    const overlapMax = Math.min(aMax, bMax)

    if (overlapMin >= overlapMax) return null

    return overlapMax - overlapMin
  }

  if (a.start.x !== b.start.x) return null

  const aMin = Math.min(a.start.y, a.end.y)
  const aMax = Math.max(a.start.y, a.end.y)
  const bMin = Math.min(b.start.y, b.end.y)
  const bMax = Math.max(b.start.y, b.end.y)
  const overlapMin = Math.max(aMin, bMin)
  const overlapMax = Math.min(aMax, bMax)

  if (overlapMin >= overlapMax) return null

  return overlapMax - overlapMin
}

export const cartesianSegmentsOverlap = (
  a: CartesianSegment,
  b: CartesianSegment,
  minOverlapLength = MIN_BEAM_WALL_OVERLAP_LENGTH,
): boolean => {
  const overlapLength = getCartesianSegmentsOverlapLength(a, b)

  if (overlapLength === null) return false

  return overlapLength >= minOverlapLength
}

export const wallToCartesianSegment = (wall: ShapeObject): CartesianSegment | null => {
  if (!wallIsCartesian(wall)) return null

  return {
    start: { x: wall.shape.points[0].x, y: wall.shape.points[0].y },
    end: { x: wall.shape.points[1].x, y: wall.shape.points[1].y },
  }
}

export const beamToCartesianSegment = (beam: ShapeObjectLine): CartesianSegment | null => {
  const start = beam.shape.start
  const end = beam.shape.end

  if (
    !lineIsCartesian(
      new ImmutableVector3(start.x, start.y, start.z),
      new ImmutableVector3(end.x, end.y, end.z),
    )
  ) {
    return null
  }

  return {
    start: { x: start.x, y: start.y },
    end: { x: end.x, y: end.y },
  }
}

export const cartesianBeamOverlapsWall = (
  beam: ShapeObjectLine,
  wall: ShapeObject,
  minOverlapLength = MIN_BEAM_WALL_OVERLAP_LENGTH,
): boolean => {
  const beamSegment = beamToCartesianSegment(beam)
  const wallSegment = wallToCartesianSegment(wall)

  if (!beamSegment || !wallSegment) return false

  return cartesianSegmentsOverlap(beamSegment, wallSegment, minOverlapLength)
}
